/**
 * Vorlage „Entkernen“.
 *
 * Draufsicht auf eine Apfel- oder Paprikahälfte mit der Schnittfläche nach
 * oben. Die Messerspitze schneidet das Kerngehäuse keilförmig (Apfel) oder
 * rund um den Stielansatz (Paprika) heraus; das Kerngehäuse wird
 * herausgehoben und zur Seite gelegt.
 */
import { ease, lerp, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { ellipsePath, path, polyline } from '../paths.ts';
import { circle, compact, faded, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { KNIFE_BLADE, board, knife } from '../parts/knife.ts';
import { arrow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Mitte der Hälfte. */
const C = { x: 120, y: 76 } as const;
const KNIFE_SCALE = 0.62;
const BLADE = KNIFE_BLADE * KNIFE_SCALE;

/** Zeitplan. */
const TIME = {
  appear: [0, 0.05],
  cutA: [0.08, 0.3],
  cutB: [0.34, 0.56],
  lift: [0.6, 0.76],
  knifeAway: [0.56, 0.64],
  vanish: [0.93, 1],
} as const;

/** Früchte. */
const ITEMS = { apple: 'Apfel', pepper: 'Paprika' } as const;

/** Form einer Frucht: Hälfte, Kerngehäuse als Pfad, Schnittlinien. */
interface Fruit {
  readonly half: SceneNode[];
  readonly core: string;
  readonly coreDetails: SceneNode[];
  readonly cuts: readonly (readonly [Point, Point])[];
}

/** Apfelhälfte: Kerngehäuse als Keil von oben nach unten. */
function apple(): Fruit {
  const outline = path()
    .move(C.x, C.y - 40)
    .curve(C.x + 30, C.y - 52, C.x + 62, C.y - 22, C.x + 50, C.y + 16)
    .curve(C.x + 40, C.y + 48, C.x + 10, C.y + 46, C.x, C.y + 40)
    .curve(C.x - 10, C.y + 46, C.x - 40, C.y + 48, C.x - 50, C.y + 16)
    .curve(C.x - 62, C.y - 22, C.x - 30, C.y - 52, C.x, C.y - 40)
    .close()
    .toString();
  const core = polyline(
    [
      { x: C.x - 16, y: C.y - 36 },
      { x: C.x + 16, y: C.y - 36 },
      { x: C.x + 4, y: C.y + 38 },
      { x: C.x - 4, y: C.y + 38 },
    ],
    true,
  );
  const seed = (x: number, y: number, turn: number): SceneNode =>
    group(
      [
        shape(
          path()
            .move(x, y - 5)
            .quad(x + 4, y, x, y + 5)
            .quad(x - 4, y, x, y - 5)
            .close()
            .toString(),
          styles.solid('deep'),
        ),
      ],
      {
        transform: { rotate: turn, ox: x, oy: y },
      },
    );
  return {
    half: [
      shape(outline, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.outline }),
      shape(ellipsePath(C.x, C.y, 34, 32), { fill: 'none', stroke: 'tint', strokeWidth: STROKE.detail }),
      line(C.x, C.y - 40, C.x - 3, C.y - 50, { stroke: 'ink', strokeWidth: STROKE.outline }),
    ],
    core,
    coreDetails: [
      shape(
        path()
          .move(C.x, C.y - 30)
          .quad(C.x - 9, C.y, C.x, C.y + 30)
          .quad(C.x + 9, C.y, C.x, C.y - 30)
          .close()
          .toString(),
        {
          fill: 'tint',
          stroke: 'shade',
          strokeWidth: STROKE.fine,
        },
      ),
      seed(C.x - 3, C.y - 8, -20),
      seed(C.x + 3, C.y + 6, 20),
    ],
    cuts: [
      [
        { x: C.x - 16, y: C.y - 36 },
        { x: C.x - 4, y: C.y + 38 },
      ],
      [
        { x: C.x + 16, y: C.y - 36 },
        { x: C.x + 4, y: C.y + 38 },
      ],
    ],
  };
}

/** Paprikahälfte: Stielansatz mit Kernen oben, weiße Rippen. */
function pepper(): Fruit {
  const outline = path()
    .move(C.x - 46, C.y - 30)
    .curve(C.x - 20, C.y - 46, C.x + 20, C.y - 46, C.x + 46, C.y - 30)
    .curve(C.x + 58, C.y, C.x + 40, C.y + 46, C.x, C.y + 44)
    .curve(C.x - 40, C.y + 46, C.x - 58, C.y, C.x - 46, C.y - 30)
    .close()
    .toString();
  const inner = path()
    .move(C.x - 38, C.y - 24)
    .curve(C.x - 16, C.y - 36, C.x + 16, C.y - 36, C.x + 38, C.y - 24)
    .curve(C.x + 48, C.y, C.x + 32, C.y + 36, C.x, C.y + 35)
    .curve(C.x - 32, C.y + 36, C.x - 48, C.y, C.x - 38, C.y - 24)
    .close()
    .toString();
  const core = ellipsePath(C.x, C.y - 20, 18, 14);
  return {
    half: [
      shape(outline, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.outline }),
      shape(inner, { fill: 'tint', stroke: 'none' }),
      line(C.x - 20, C.y - 10, C.x - 26, C.y + 26, { stroke: 'paper', strokeWidth: STROKE.outline }),
      line(C.x + 20, C.y - 10, C.x + 26, C.y + 26, { stroke: 'paper', strokeWidth: STROKE.outline }),
    ],
    core,
    coreDetails: [
      shape(core, { fill: 'paper', stroke: 'shade', strokeWidth: STROKE.fine }),
      ...[
        [-8, -4],
        [-2, -10],
        [6, -6],
        [9, 2],
        [-6, 4],
        [2, 6],
      ].map(([dx, dy]) =>
        circle(C.x + (dx ?? 0), C.y - 20 + (dy ?? 0), 2, {
          fill: 'snow',
          stroke: 'deep',
          strokeWidth: STROKE.fine,
        }),
      ),
      shape(ellipsePath(C.x, C.y - 32, 6, 5), styles.solid('shade')),
    ],
    cuts: [
      [
        { x: C.x - 20, y: C.y - 30 },
        { x: C.x, y: C.y - 4 },
      ],
      [
        { x: C.x + 20, y: C.y - 30 },
        { x: C.x, y: C.y - 4 },
      ],
    ],
  };
}

/** Messerpose: Spitze auf dem Punkt tip, Klinge zeigt in Richtung angle (Grad) zum Griff. */
function tipPose(tip: Point, angle: number, lift: number) {
  const radians = (angle * Math.PI) / 180;
  return { x: tip.x + Math.cos(radians) * BLADE, y: tip.y + Math.sin(radians) * BLADE, rotate: angle, lift };
}

/** Vorlage „Entkernen“. */
export const cutCoreTemplate = defineTemplate({
  id: 'cut.core',
  group: 'cut',
  title: 'Entkernen',
  description:
    'Mit der Messerspitze wird das Kerngehäuse herausgeschnitten: beim Apfel als Keil, bei der Paprika rund um den Stielansatz mit den Kernen.',
  durationSeconds: 6,
  keywords: ['entkernen', 'entkernt', 'kerngehäuse', 'kerne entfernen', 'stielansatz', 'vierteln'],
  stillTime: 0.7,
  parameters: [choice('item', 'Frucht', ITEMS, 'apple')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/apfel|äpfel|birne/.test(lower)) return { item: 'apple' };
    if (/paprika|schote/.test(lower)) return { item: 'pepper' };
    return {};
  },
  scene(values, t) {
    const fruit = textValue(values, 'item', 'apple') === 'pepper' ? pepper() : apple();
    const visible = Math.min(
      ease.outCubic(segment(t, ...TIME.appear)),
      1 - ease.inCubic(segment(t, ...TIME.vanish)),
    );
    const lift = ease.inOutCubic(segment(t, ...TIME.lift));
    const cutA = segment(t, ...TIME.cutA);
    const cutB = segment(t, ...TIME.cutB);

    // Schnittlinien, die mit dem Messer wachsen.
    const cutLine = (index: number, amount: number): SceneNode | null => {
      const segmentPoints = fruit.cuts[index];
      if (segmentPoints === undefined || amount <= 0) {
        return null;
      }
      const [from, to] = segmentPoints;
      return line(from.x, from.y, lerp(from.x, to.x, amount), lerp(from.y, to.y, amount), styles.detail());
    };
    const coreGroup = group(
      [
        group(fruit.coreDetails, { clip: fruit.core }),
        shape(fruit.core, { stroke: 'ink', strokeWidth: STROKE.detail }),
      ],
      {
        transform: { x: -lift * 80, y: lift * 26, rotate: -lift * 30, ox: C.x, oy: C.y },
      },
    );
    const hole =
      lift > 0 ? shape(fruit.core, { fill: 'tint', stroke: 'ink', strokeWidth: STROKE.detail }) : null;

    // Messer: schneidet erst die eine, dann die andere Linie, dann zur Seite.
    const pick = (amount: number, index: number) => {
      const segmentPoints = fruit.cuts[index] ?? fruit.cuts[0];
      const [from, to] = segmentPoints ?? [C, C];
      return { x: lerp(from.x, to.x, amount), y: lerp(from.y, to.y, amount) };
    };
    const away = ease.inOutCubic(segment(t, ...TIME.knifeAway));
    const into = ease.inOutCubic(segment(t, 0.04, 0.08));
    const tip = t < TIME.cutB[0] ? pick(ease.inOutSine(cutA), 0) : pick(ease.inOutSine(cutB), 1);
    // Griff zur Seite, damit das Messer im Bild bleibt.
    const angle = t < TIME.cutB[0] ? 172 : 8;
    const knifeNode = knife({
      ...tipPose(
        { x: lerp(tip.x, 116, away), y: lerp(tip.y, 128, away) },
        lerp(angle, 0, away),
        lerp(0.1, 0.6, away),
      ),
      scale: KNIFE_SCALE,
      squash: lerp(0.5, 1, away),
    });
    return compact([
      ...board(),
      faded(visible, [...fruit.half, hole, cutLine(0, cutA), cutLine(1, cutB), coreGroup]),
      faded(into * (1 - segment(t, 0.9, 0.95)), [knifeNode]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: C.x - 10, y: C.y },
        { x: C.x - 50, y: C.y + 24 },
        { x: C.x - 80, y: C.y + 30 },
      ]),
    ];
  },
});
